import posthog from 'posthog-js';
import { trackPostHogEvent } from './analytics';

// Initialize PostHog
export const initPostHog = () => {
  if (typeof window !== 'undefined') {
    posthog.init('phc_GeoGBENnz4uIV1Oq9NrfjYyaOQTEOXrvGKHbhLJShfh', {
      api_host: 'https://us.i.posthog.com',
      person_profiles: 'always',
      capture_pageview: false,
      capture_pageleave: true,
      disable_session_recording: import.meta.env.DEV,
      session_recording: {
        recordCrossOriginIframes: false,
      },
      loaded: () => {
        if (import.meta.env.DEV) {
          console.log('PostHog initialized successfully');
        }
      },
    });
  }
};

// Helper to track events (uses central tracking function)
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  trackPostHogEvent(eventName, properties);
};

// Identify user
export const identifyUser = (userId: string, traits?: Record<string, any>) => {
  if (typeof window !== 'undefined') {
    posthog.identify(userId, traits);
  }
};

// Reset user
export const resetUser = () => {
  if (typeof window !== 'undefined') {
    posthog.reset();
  }
};

// Specific tracking helpers
export const trackScrollDepth = (percentage: number) => {
  trackPostHogEvent('scroll_depth_reached', {
    scroll_percentage: `${percentage}%`,
  });
};

export const trackPageView = (path: string, title: string, referrer?: string) => {
  trackPostHogEvent('page_viewed', {
    page_path: path,
    referrer: referrer || document.referrer,
  });
};

export const trackFormOpened = (triggerLocation: string, triggerText: string) => {
  trackPostHogEvent('form_opened', {
    form_name: 'Lead Generation Form',
    trigger_location: triggerLocation,
    trigger_text: triggerText,
  });
};

export const trackFormStepViewed = (stepNumber: number, stepName: string, isSkippable: boolean) => {
  trackPostHogEvent('form_step_viewed', {
    form_name: 'Lead Generation Form',
    step_number: stepNumber,
    step_name: stepName,
    is_skippable: isSkippable,
  });
};

export const trackFormStepAbandoned = (
  abandonedStepNumber: number,
  abandonedStepName: string,
  formDataCaptured: Record<string, any>,
  timeSpentSeconds?: number
) => {
  trackPostHogEvent('form_step_abandoned', {
    form_name: 'Lead Generation Form',
    abandoned_step_number: abandonedStepNumber,
    abandoned_step_name: abandonedStepName,
    form_data_captured: formDataCaptured,
    ...(timeSpentSeconds !== undefined && { time_spent_seconds: timeSpentSeconds }),
  });
};

export const trackFormSubmitted = (
  eventType: string,
  participantsCount: string,
  budgetRange: string,
  locationSelected: string,
  hasSpecificDates: boolean,
  completionTimeSeconds: number
) => {
  trackPostHogEvent('brief_submitted', {
    form_name: 'Lead Generation Form',
    event_type: eventType,
    participants_count: participantsCount,
    budget_range: budgetRange,
    location_selected: locationSelected,
    has_specific_dates: hasSpecificDates,
    completion_time_seconds: completionTimeSeconds,
  });
};

export const trackFormTriggerClicked = (
  triggerElementText: string,
  triggerLocation: string,
  triggerType: 'button' | 'card' | 'link'
) => {
  trackPostHogEvent('form_trigger_clicked', {
    trigger_element_text: triggerElementText,
    trigger_location: triggerLocation,
    trigger_type: triggerType,
  });
};

export const trackCollectionItemClicked = (
  collectionName: string,
  collectionCategory: string,
  location: string,
  actionTaken: 'form_opened' | 'details_viewed'
) => {
  trackPostHogEvent('collection_item_clicked', {
    collection_name: collectionName,
    collection_category: collectionCategory,
    location: location,
    action_taken: actionTaken,
  });
};

export const trackBudgetOptionSelected = (budgetRange: string) => {
  trackPostHogEvent('budget_option_selected', {
    budget_range: budgetRange,
  });
};

export const trackExpertClicked = (expertName: string) => {
  trackPostHogEvent('expert_clicked', {
    expert_name: expertName,
  });
};

export const trackTalkTeamClicked = (
  modalOpened: boolean,
  meetingTaken: boolean,
  abandoned: boolean
) => {
  trackPostHogEvent('talk_team_clicked', {
    modal_opened: modalOpened,
    meeting_taken: meetingTaken,
    abandoned: abandoned,
  });
};

export const trackDestinationClicked = (
  destinationName: string,
  destinationRegion: 'northAmerica' | 'latinAmerica' | 'europe',
  actionTaken: 'form_opened'
) => {
  trackPostHogEvent('destination_clicked', {
    destination_name: destinationName,
    destination_region: destinationRegion,
    action_taken: actionTaken,
  });
};
